import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";

export function isEmptyField(value: any) {
  if (isNullOrUndefined(value) || value === '') {
    return true;
  }
  return false;
}

export function isEmptyArray(value: any[]) {
  if (isEmptyField(value)) {
    return true;
  }
  return value.length === 0;
}

export function isEmptyObject(value: any) {
  if (isEmptyField(value)) {
    return true;
  }
  return Object.keys(value).length === 0;
}

export function getFormattedDate(value?: any) {
  if (isEmptyField(value)) {
    return new Date().toISOString().slice(0, 10).replace(/-/g, '');
  } else {
    return new Date(value).toISOString().slice(0, 10).replace(/-/g, '');
  }
}

export function isNullOrUndefined(value: any) {
  if (value == null || value == undefined) {
    return true
  }
  return false;
}

export function convertToFloat(value: any, fixedTo?: any) {
  if (isEmptyField(value)) {
    return '-';
  }
  if (isNaN(value)) {
    return value;
  }
  value = parseFloat(value);
  if (!isEmptyField(fixedTo)) {
    value = parseFloat(value).toFixed(fixedTo);
    value = parseFloat(value);
  }
  return value;
}

export function convertToInt(value: any) {
  if (isEmptyField(value)) {
    return '-';
  }
  if (isNaN(value)) {
    return value;
  }
  return parseInt(value, 10);
}

export function isSameArray(arr1: any[], arr2: any[]) {
  let isSame = true;
  for (let index = 0; index < arr2.length; index++) {
    if (isEmptyField(arr1[index])) {
      isSame = false;
      break;
    }
    if (arr2.indexOf(arr1[index]) == -1) {
      isSame = false;
      break;
    }
  }
  return isSame;
}

export function isObject(a: any) {
  return (!!a) && (a.constructor === Object);
}
export function toInteger(value: any): number {
  return parseInt(`${value}`, 10);
}

export function toString(value: any): string {
  return (value !== undefined && value !== null) ? `${value}` : '';
}

export function getValueInRange(value: number, max: number, min = 0): number {
  return Math.max(Math.min(value, max), min);
}

export function isString(value: any): value is string {
  return typeof value === 'string';
}

export function isNumber(value: any): value is number {
  return !isNaN(toInteger(value));
}

export function isInteger(value: any): value is number {
  return typeof value === 'number' && isFinite(value) && Math.floor(value) === value;
}

export function isDefined(value: any): boolean {
  return value !== undefined && value !== null;
}

export function padNumber(value: number) {
  if (isNumber(value)) {
    return `0${value}`.slice(-2);
  } else {
    return '';
  }
}

export function regExpEscape(text: string) {
  return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

export function hasClassName(element: any, className: string): boolean {
  return element?.className?.split(/\s+/)?.indexOf(className) >= 0;
}

if (typeof Element !== 'undefined' && !Element.prototype.closest) {
  // Polyfill for ie10+

  if (!Element.prototype.matches) {
    // IE uses the non-standard name: msMatchesSelector
    Element.prototype.matches = (Element.prototype as any).msMatchesSelector;
  }

  Element.prototype.closest = function (s: string) {
    let el: any = this;
    if (!document.documentElement.contains(el)) {
      return null;
    }
    do {
      if (el.matches(s)) {
        return el;
      }
      el = el.parentElement || el.parentNode;
    } while (el !== null && el.nodeType === 1);
    return null;
  };
}

export function closest(element: HTMLElement, selector: any) {
  if (!selector) {
    return null;
  }
  return element.closest(selector);
}

export function isDataAvailable(data: any): boolean {
  if (isNullOrUndefined(data)) {
    return false;
  }

  if (Array.isArray(data)) {
    return data.length > 0;
  }

  return !isEmptyObject(data);
}


export function toDelay(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}
export function noWhitespaceValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const isWhitespace = (control.value || '').trim().length !== (control.value || '').length;
    const isValid = !isWhitespace;
    return isValid ? null : { 'whitespace': true };
  };
}

export function hasUpperCase(control: AbstractControl): ValidationErrors | null {
  const hasUpper = /[A-Z]/.test(control.value);
  return hasUpper ? null : { missingUpperCase: true };
}

export function hasLowerCase(control: AbstractControl): ValidationErrors | null {
  const hasLower = /[a-z]/.test(control.value);
  return hasLower ? null : { missingLowerCase: true };
}

export function hasSpecialCharacter(control: AbstractControl): ValidationErrors | null {
  const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(control.value);
  return hasSpecial ? null : { missingSpecialCharacter: true };
}

export function hasTwoNumbers(control: AbstractControl): ValidationErrors | null {
  const hasTwoNums = /\d.*\d/.test(control.value);
  return hasTwoNums ? null : { missingTwoNumbers: true };
}

export function passwordsMatchValidator(formGroup: AbstractControl): ValidationErrors | null {
  const newPassword = formGroup.get('newPassword')?.value;
  const confirmPassword = formGroup.get('confirmPassword')?.value;
  return newPassword === confirmPassword ? null : { passwordsMismatch: true };
}

export function noConsecutiveSpaces(): ValidationErrors | null {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const value = /\b\s{2,}\b/.test(control.value);
    return value ? { consecutiveSpaces: true } : null;
  };
}

export class CustomValidators {
  // Validator to ensure no only numbers
  static noOnlyNumbersValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value && /^\d+$/.test(value)) {
        return { noOnlyNumbers: true };
      }
      return null;
    };
  }

  // Validator to ensure no leading/trailing whitespace
  static noWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    const isWhitespace = (control.value || '').trim().length !== control.value.length;
    const isValid = !isWhitespace;
    return isValid ? null : { whitespace: true };
  }

  static noConsecutiveSpaces(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }
    const value = /\b\s{2,}\b/.test(control.value);
    return value ? { consecutiveSpaces: true } : null;
  }

  /**
  * Validates that the input value is a positive number.
  * 
  * @param control - The form control to validate.
  * @returns `{ negativeNumber: true }` if the value is 0 or negative, otherwise `null`.
  */
  static noNegativeNumbers(control: AbstractControl): ValidationErrors | null {
    if(!control.value) {
      return { vaildNumber: true };
    }
    return control.value > 0 ? null : { negativeNumber: true }
  }

  /*
  * Validates that the input is a strictly valid URL.
  * - Must not contain spaces
  * - Must start with http:// or https:// based on flags
  * - Must have a valid hostname with a dot (e.g., google.com)
  */
  static isStrictValidUrl(acceptHttp: boolean = true, acceptHttps: boolean = true): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) return null;

      if (value.includes(' ')) {
        return { invalidUrl: true }; // Disallow spaces
      }

      try {
        const url = new URL(value);
        const scheme = url.protocol;

        const isHttp = scheme === 'http:';
        const isHttps = scheme === 'https:';
        const isAllowedScheme =
          (acceptHttp && isHttp) || (acceptHttps && isHttps);

        const hasValidDomain =
          url.hostname.includes('.') && !url.hostname.endsWith('.');

        return isAllowedScheme && hasValidDomain ? null : { invalidUrl: true };
      } catch {
        return { invalidUrl: true };
      }
    };
  }

  /**
   * Validates that all placeholders in the control's value are strictly of the form {{number}}.
   * Returns a validation error if any placeholder is invalid or if there are duplicate numbers.
   * Placeholders must be unique and match the pattern {{number}} exactly.
   * Returns null if all placeholders are valid and unique.
   *
   * @param control - The form control containing the text to validate.
   * @returns ValidationErrors object if invalid, or null if valid.
   */
  static validatePlaceholders(control: AbstractControl): ValidationErrors | null {
    const text: string = control.value || '';

    // Extract all placeholders like {{anything}}
    const allPlaceholders = text.match(/\{\{.*?\}\}/g) || [];

    // Identify placeholders that are NOT strictly {{number}}
    const invalidPlaceholders = allPlaceholders.filter(ph => !/^\{\{\d+\}\}$/.test(ph));
    if (invalidPlaceholders.length > 0) {
      return { invalidPlaceholders: true };
    }

    // Extract numbers from valid placeholders
    const numbers = allPlaceholders.map(ph => parseInt(ph.slice(2, -2), 10));

    // Detect duplicates using a Set
    const seen = new Set<number>();
    const hasDuplicates = numbers.some(num => {
      if (seen.has(num)) return true;
      seen.add(num);
      return false;
    });
    if (hasDuplicates) {
      return { duplicatePlaceholders: true };
    }

    return null; // valid
  }
}

// HH:MM:SS time convert in second
export function convertToSeconds(time: string): number {
  if (!time || typeof time !== 'string') {
    return 0;
  }
  const [hours, minutes, seconds] = time.split(':').map(Number);
  return hours * 3600 + minutes * 60 + seconds;
}

export function convertSecondsToHHMMSS(seconds: number | string): string {
  const totalSeconds = typeof seconds === 'string' ? parseInt(seconds, 10) : seconds;
  if (isNaN(totalSeconds) || totalSeconds < 0) {
    return '00:00:00';
  }
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = Math.floor(totalSeconds % 60);

  return `${padToTwo(hours)}:${padToTwo(minutes)}:${padToTwo(secs)}`;
}

export function padToTwo(num: number): string {
  return num.toString().padStart(2, '0');
}

export function extractBaseUrl(url: string): string {
  const parsedUrl = new URL(url);
  return `${parsedUrl.protocol}//${parsedUrl.hostname}`;
}

/**
 * Validator function to detect duplicates in a FormArray.
 * 
 * @param controlsAccessor - Function that returns the array of controls to compare against.
 * @returns ValidatorFn that flags duplicates with a `{ duplicate: true }` error.
 */
export function duplicateInArrayValidator(controlsAccessor: () => AbstractControl[]): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const value = control.value?.trim()?.toLowerCase();
    if (!value) return null;

    const controls = controlsAccessor();
    const duplicate = controls.some(
      c => c !== control && c.value?.trim()?.toLowerCase() === value
    );

    return duplicate ? { duplicate: true } : null;
  };
}


export function convertHtmlToWhatsAppFormat(html: string): string {
  return html
    .replace(/<strong>(.*?)<\/strong>/gi, (_, content) => `*${content.trim()}*`)
    .replace(/<b>(.*?)<\/b>/gi, (_, content) => `*${content.trim()}*`)
    .replace(/<em>(.*?)<\/em>/gi, (_, content) => `_${content.trim()}_`)
    .replace(/<i>(.*?)<\/i>/gi, (_, content) => `_${content.trim()}_`)
    .replace(/<s>(.*?)<\/s>/gi, (_, content) => `~${content.trim()}~`)
    .replace(/<del>(.*?)<\/del>/gi, (_, content) => `~${content.trim()}~`)
    .replace(/<code>(.*?)<\/code>/gi, (_, content) => `\`${content.trim()}\``)
    .replace(/<\/p>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .trim();
}

export function convertWhatsAppFormatToHtml(text: string): string {
  if (!text) return '';

  return text
    // Escape HTML to prevent injection
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // WhatsApp formatting
    .replace(/\*(.*?)\*/g, '<strong>$1</strong>')     // *bold*
    .replace(/_(.*?)_/g, '<em>$1</em>')               // _italic_
    .replace(/`(.*?)`/g, '<code>$1</code>')           // `code`
    .replace(/~(.*?)~/g, '<s>$1</s>')                 // ~strikethrough~
    .replace(/\n/g, '<br>');                          // newline to <br>
}